import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';

import {
  loadMedia,
  loadMediaSummary,
  trashItem,
  restoreItem,
  bulkTrash,
  bulkRestore,
  bulkPurge,
  emptyTrash,
  loadDuplicates,
  loadUploaders,
  recalculateUsage,
  setFilters,
  setDuplicateFilters,
  mediaItemAdded,
  mediaItemTrashed,
  mediaItemRestored,
  mediaItemPurged,
  selectMediaItems,
  selectMediaPagination,
  selectMediaStatus,
  selectMediaLoadedOnce,
  selectMediaPendingIds,
  selectMediaBulkInFlight,
} from '../../../redux/Slices/workspaceMediaSlice';
import { fetchMediaDownloadUrl } from '../../../api/workspaceMediaApi';
import { showSuccess, showError } from '../../../utils/snackbarHelpers';
import { fetchWorkspaces } from '../../../redux/Slices/workspaceSlice';
import { useSocketAction } from '../../../hooks/useSocketManager';
import ZoobbeSelect from '../../Global/ZoobbeSelect';
import ImagePlaceholder from '../../Global/ImagePlaceholder';
import StorageMeter, { formatBytes } from './StorageMeter';
import ConfirmDialog from './ConfirmDialog';
import MediaViewer from './MediaViewer';

import {
  IconClose, IconChevronLeft, IconChevronRight, IconSearch, IconSchedule,
  IconDelete, IconDeleteForever, IconRestore, IconSpinner, IconDownload, IconOpenInNew, IconCopyAll, IconRefresh,
  IconGridView, IconViewList, IconSelectAll, IconDeselect,
  IconDescription, IconImage, IconFolderOpen,
  IconPdf, IconVideo, IconAudio, IconArchive, IconSheet, IconSlides, IconNotes, IconFile,
} from './icons';
import './WorkspaceMedia.scss';

/** Image that swaps to a fallback icon when the src fails to load. */
const Thumb = ({ src, alt, FallbackIcon, variant = 'card' }) => {
  const [failed, setFailed] = React.useState(false);
  if (!src || failed) {
    if (variant === 'row') return <FallbackIcon size={18} />;
    return (
      <div className="wm__card-icon">
        <FallbackIcon size={48} />
      </div>
    );
  }
  return <img src={src} alt={alt || ''} loading="lazy" onError={() => setFailed(true)} />;
};

const SOURCE_OPTIONS = [
  { value: '', label: 'All sources' },
  { value: 'card', label: 'Card attachments' },
  { value: 'page', label: 'Page attachments' },
  { value: 'page-cover', label: 'Page covers' },
];

const SORT_OPTIONS = [
  { value: '-uploadedAt', label: 'Newest' },
  { value: 'uploadedAt', label: 'Oldest' },
  { value: '-size', label: 'Largest' },
  { value: 'size', label: 'Smallest' },
  { value: 'name', label: 'Name (A → Z)' },
];

const DATE_RANGE_OPTIONS = [
  { label: 'All time', value: '' },
  { label: 'Last 7 days', value: '7' },
  { label: 'Last 30 days', value: '30' },
  { label: 'Last 90 days', value: '90' },
  { label: 'Older than 6 months', value: 'older' },
];

const dateRangeToFilter = (value) => {
  if (!value) return { uploadedFrom: '', uploadedTo: '' };
  if (value === 'older') {
    const d = new Date();
    d.setMonth(d.getMonth() - 6);
    return { uploadedFrom: '', uploadedTo: d.toISOString() };
  }
  const days = parseInt(value, 10);
  if (!Number.isFinite(days)) return { uploadedFrom: '', uploadedTo: '' };
  const d = new Date();
  d.setDate(d.getDate() - days);
  return { uploadedFrom: d.toISOString(), uploadedTo: '' };
};

const filterToDateRange = (filters) => {
  if (!filters.uploadedFrom && !filters.uploadedTo) return '';
  if (filters.uploadedTo && !filters.uploadedFrom) return 'older';
  if (!filters.uploadedFrom) return '';
  const days = Math.round((Date.now() - new Date(filters.uploadedFrom).getTime()) / 86400000);
  if ([7, 30, 90].includes(days)) return String(days);
  return '';
};

const SOURCE_LABEL = {
  card: 'Card',
  page: 'Page',
  'page-cover': 'Cover',
};

const SOURCE_ICON = {
  card: IconDescription,
  page: IconNotes,
  'page-cover': IconImage,
};

const isImage = (item) =>
  /^image\//.test(item?.fileType || '') ||
  /\.(png|jpe?g|gif|webp|svg|avif|bmp)$/i.test(item?.name || '');

const isVideo = (item) => /^video\//.test(item?.fileType || '');

const fileIconFor = (item) => {
  const t = (item?.fileType || '').toLowerCase();
  const n = (item?.name || '').toLowerCase();
  if (t.includes('pdf') || n.endsWith('.pdf')) return IconPdf;
  if (isVideo(item)) return IconVideo;
  if (t.startsWith('audio/')) return IconAudio;
  if (t.includes('zip') || t.includes('rar') || /\.(zip|rar|7z|tar|gz)$/.test(n)) return IconArchive;
  if (t.includes('sheet') || t.includes('excel') || /\.(xlsx?|csv)$/.test(n)) return IconSheet;
  if (t.includes('word') || /\.(docx?|odt|rtf)$/.test(n)) return IconDescription;
  if (t.includes('presentation') || /\.(pptx?|odp)$/.test(n)) return IconSlides;
  if (t.includes('text/') || /\.(txt|md)$/.test(n)) return IconNotes;
  return IconFile;
};

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const daysUntil = (iso) => {
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
};

const Uploader = ({ user }) => {
  if (!user) return <span className="wm__uploader wm__uploader--empty">—</span>;
  const name = user.name || user.username || 'Unknown';
  return (
    <span className="wm__uploader" title={name}>
      {user.profilePicture ? (
        <img src={user.profilePicture} alt={name} className="wm__uploader-avatar" loading="lazy" />
      ) : (
        <ImagePlaceholder className="wm__uploader-avatar" size={18} member={user} text={name} onlineIndicator={false} />
      )}
      <span className="wm__uploader-name">{name}</span>
    </span>
  );
};

const DUPLICATE_SORT_OPTIONS = [
  { label: 'Most wasted space', value: '-wastedBytes' },
  { label: 'Most copies', value: '-count' },
  { label: 'Largest file', value: '-size' },
  { label: 'Name (A → Z)', value: 'name' },
];

const DuplicatesPanel = ({
  duplicates, onFilterChange,
  expandedGroup, setExpandedGroup,
  onOpenSource, onDownload, onTrashOne, onKeepOldest,
  isAdmin, currentUserId,
}) => {
  const { filters, status, loadedOnce, groups, totalGroups, totalWasted } = duplicates;
  const isFiltered = !!(filters.source || filters.q);
  const isLoading = status === 'loading';

  const toolbar = (
    <div className="wm__toolbar">
      <div className="wm__search">
        <IconSearch size={18} />
        <input
          type="search"
          placeholder="Search duplicates…"
          value={filters.q}
          onChange={(e) => onFilterChange({ q: e.target.value })}
        />
      </div>
      <div className="wm__filters">
        <ZoobbeSelect
          className="wm__select"
          options={SOURCE_OPTIONS}
          value={filters.source}
          onSelect={(opt) => onFilterChange({ source: opt.value })}
        />
        <ZoobbeSelect
          className="wm__select"
          options={DUPLICATE_SORT_OPTIONS}
          value={filters.sort}
          onSelect={(opt) => onFilterChange({ sort: opt.value })}
        />
      </div>
    </div>
  );

  if (isLoading && !loadedOnce) {
    return (
      <div className="wm__duplicates">
        {toolbar}
        <div className="wm__empty"><IconSpinner size={24} className="wm__spin" /><p>Scanning for duplicates…</p></div>
      </div>
    );
  }
  if (!groups.length) {
    return (
      <div className="wm__duplicates">
        {toolbar}
        <div className="wm__empty">
          <div className="wm__empty-icon"><IconCopyAll size={32} /></div>
          <h3>{isFiltered ? 'No duplicates match your filters' : 'No duplicates found'}</h3>
          <p>
            {isFiltered
              ? 'Try a different search or source.'
              : 'Every file in this workspace is unique. Nice and tidy.'}
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="wm__duplicates">
      {toolbar}
      <div className="wm__duplicates-summary">
        <strong>{totalGroups}</strong> duplicate group{totalGroups === 1 ? '' : 's'} ·
        wasting <strong>{formatBytes(totalWasted)}</strong> of storage
        {isFiltered && <span className="wm__dot">·</span>}
        {isFiltered && (
          <button className="wm__link" onClick={() => onFilterChange({ source: '', q: '' })}>
            Clear filters
          </button>
        )}
      </div>
      {duplicates.groups.map((group) => {
        const open = expandedGroup === group.fileHash;
        return (
          <div key={group.fileHash} className={`wm__dup-group ${open ? 'is-open' : ''}`}>
            <div className="wm__dup-head">
              <button
                className="wm__dup-toggle"
                onClick={() => setExpandedGroup(open ? null : group.fileHash)}
                title={open ? 'Collapse' : 'Expand'}
              >
                <IconChevronRight size={16} className={open ? 'wm__dup-chevron is-open' : 'wm__dup-chevron'} />
              </button>
              <div className="wm__dup-name" title={group.name}>{group.name}</div>
              <div className="wm__dup-meta">
                <span className="wm__dup-count">{group.count} copies</span>
                <span className="wm__dot">·</span>
                <span>{formatBytes(group.size)} each</span>
                <span className="wm__dot">·</span>
                <span className="wm__dup-wasted">{formatBytes(group.wastedBytes)} wasted</span>
              </div>
              <button
                className="wm__btn wm__btn--ghost"
                onClick={() => onKeepOldest(group)}
                title="Trash all copies except the oldest"
              >
                <IconDelete size={16} />
                Keep oldest
              </button>
            </div>
            {open && (
              <div className="wm__dup-items">
                {group.items.map((item, idx) => {
                  const canDelete = isAdmin || String(item.uploader?._id || item.uploader) === String(currentUserId);
                  return (
                    <div key={item._id} className="wm__dup-item">
                      <span className={`wm__dup-badge ${idx === 0 ? 'is-oldest' : ''}`}>
                        {idx === 0 ? 'Oldest' : `Copy ${idx + 1}`}
                      </span>
                      <span className="wm__dup-item-meta">
                        Uploaded {formatDate(item.uploadedAt)}
                        {item.uploader && <> by {item.uploader.username || item.uploader.name}</>}
                      </span>
                      <div className="wm__dup-item-actions">
                        {item.sourceUrl && (
                          <button className="wm__icon-btn" onClick={() => onOpenSource(item)} title="Open source">
                            <IconOpenInNew size={16} />
                          </button>
                        )}
                        <button className="wm__icon-btn" onClick={() => onDownload(item)} title="Download">
                          <IconDownload size={16} />
                        </button>
                        {canDelete && (
                          <button
                            className="wm__icon-btn wm__icon-btn--danger"
                            onClick={() => onTrashOne(item)}
                            title="Move to Trash"
                          >
                            <IconDelete size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const WorkspaceMedia = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { shortId } = useParams();
  const workspaceId = shortId;

  const summary = useSelector((s) => s.workspaceMedia.summary);
  const filters = useSelector((s) => s.workspaceMedia.filters);
  const items = useSelector(selectMediaItems);
  const pagination = useSelector(selectMediaPagination);
  // Bucket-specific totals so each tab badge shows its own count, not the
  // active bucket's.
  const allCount = useSelector((s) => s.workspaceMedia.all.pagination.total);
  const trashCount = useSelector(
    (s) => s.workspaceMedia.summary?.trashedCount ?? s.workspaceMedia.trash.pagination.total
  );
  const status = useSelector(selectMediaStatus);
  const loadedOnce = useSelector(selectMediaLoadedOnce);
  const pendingIds = useSelector(selectMediaPendingIds);
  const bulkInFlight = useSelector(selectMediaBulkInFlight);
  const pendingSet = useMemo(() => new Set(pendingIds), [pendingIds]);
  const { workspaces } = useSelector((s) => s.workspaces);
  const currentUserId = useSelector((s) => s.user?.user?.user?._id || s.user?.user?._id);

  const workspace = useMemo(
    () => workspaces.find((w) => w.shortId === shortId || w._id === shortId),
    [workspaces, shortId]
  );

  const isAdmin = useMemo(() => {
    if (!workspace || !currentUserId) return false;
    if (String(workspace.ownerId) === String(currentUserId)) return true;
    return (workspace.members || []).some(
      (m) =>
        String(m.user?._id || m.user) === String(currentUserId) &&
        (m.role === 'admin' || m.role === 'owner')
    );
  }, [workspace, currentUserId]);

  const [selected, setSelected] = useState(() => new Set());
  const [tab, setTab] = useState('all'); // 'all' | 'trash' | 'duplicates'
  const [expandedGroup, setExpandedGroup] = useState(null);

  const duplicates = useSelector((s) => s.workspaceMedia.duplicates);
  const uploaders = useSelector((s) => s.workspaceMedia.uploaders.list);

  useEffect(() => {
    setSelected(new Set());
  }, [filters.trash, workspaceId, tab]);

  useEffect(() => {
    if (tab === 'duplicates' && workspaceId) {
      dispatch(loadDuplicates({ workspaceId, filters: duplicates.filters }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, workspaceId, dispatch, duplicates.filters.source, duplicates.filters.q, duplicates.filters.sort]);

  useEffect(() => {
    if (workspaceId) dispatch(loadUploaders({ workspaceId }));
  }, [workspaceId, dispatch]);

  const uploaderOptions = useMemo(() => [
    { label: 'All uploaders', value: '' },
    ...uploaders.map((u) => ({ label: `${u.name || u.username} (${u.count})`, value: u._id })),
  ], [uploaders]);

  const onDateRangeChange = useCallback((val) => {
    const range = dateRangeToFilter(val);
    dispatch(setFilters(range));
  }, [dispatch]);
  const [view, setView] = useState(() => localStorage.getItem('workspaceMediaView') || 'grid');
  const [confirm, setConfirm] = useState(null); // { title, message, confirmLabel, destructive, onConfirm }
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  // All visible items are previewable — MediaViewer handles image / video /
  // audio / pdf / unknown internally.
  const openLightboxForItem = useCallback(
    (item) => {
      const idx = items.findIndex((i) => i._id === item._id);
      if (idx >= 0) setLightboxIndex(idx);
    },
    [items]
  );

  const closeLightbox = useCallback(() => setLightboxIndex(-1), []);
  const nextLightbox = useCallback(
    () => setLightboxIndex((i) => (i + 1) % Math.max(1, items.length)),
    [items.length]
  );
  const prevLightbox = useCallback(
    () => setLightboxIndex((i) => (i - 1 + items.length) % Math.max(1, items.length)),
    [items.length]
  );

  useEffect(() => {
    if (!workspaces.length) dispatch(fetchWorkspaces());
  }, [dispatch, workspaces.length]);

  useEffect(() => {
    if (!workspaceId) return;
    dispatch(loadMediaSummary({ workspaceId }));
    dispatch(loadMedia({ workspaceId, filters, page: 1, limit: pagination.limit }));
    setSelected(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, workspaceId, filters]);

  useSocketAction(
    ['MEDIA_ITEM_ADDED', 'MEDIA_ITEM_TRASHED', 'MEDIA_ITEM_RESTORED', 'MEDIA_ITEM_PURGED'],
    (data) => {
      if (workspace && String(data.workspaceId) !== String(workspace._id) && data.workspaceId !== workspaceId) return;
      switch (data.action) {
        case 'MEDIA_ITEM_ADDED': dispatch(mediaItemAdded(data)); break;
        case 'MEDIA_ITEM_TRASHED': dispatch(mediaItemTrashed(data)); break;
        case 'MEDIA_ITEM_RESTORED': dispatch(mediaItemRestored(data)); break;
        case 'MEDIA_ITEM_PURGED': dispatch(mediaItemPurged(data)); break;
        default: break;
      }
    },
    [dispatch, workspace?._id, workspaceId]
  );

  const onFilterChange = (key, value) => dispatch(setFilters({ [key]: value }));

  const onViewChange = (next) => {
    setView(next);
    localStorage.setItem('workspaceMediaView', next);
  };

  const toggleSelect = (id, e) => {
    e?.stopPropagation?.();
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((i) => i._id)));
  };

  const dropFromSelection = useCallback((ids) => {
    setSelected((prev) => {
      if (!prev.size) return prev;
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));
      return next.size === prev.size ? prev : next;
    });
  }, []);

  const handleTrash = useCallback(
    async (itemId) => {
      try {
        await dispatch(trashItem({ workspaceId, itemId })).unwrap();
        dropFromSelection([itemId]);
        showSuccess(dispatch, 'Moved to Trash');
        dispatch(loadMediaSummary({ workspaceId }));
      } catch (err) {
        showError(dispatch, err || 'Failed to delete');
      }
    },
    [dispatch, workspaceId, dropFromSelection]
  );

  const handleRestore = useCallback(
    async (itemId) => {
      try {
        await dispatch(restoreItem({ workspaceId, itemId })).unwrap();
        dropFromSelection([itemId]);
        showSuccess(dispatch, 'Restored');
        dispatch(loadMediaSummary({ workspaceId }));
      } catch (err) {
        showError(dispatch, err || 'Failed to restore');
      }
    },
    [dispatch, workspaceId, dropFromSelection]
  );

  const handleBulkTrash = () => {
    if (!selected.size) return;
    const n = selected.size;
    setConfirm({
      title: `Move ${n} file${n === 1 ? '' : 's'} to Trash?`,
      message: `These files will be auto-deleted after ${summary?.retentionDays ?? 30} days. You can restore them before then.`,
      confirmLabel: 'Move to Trash',
      destructive: true,
      Icon: IconDelete,
      onConfirm: async () => {
        setConfirm(null);
        try {
          await dispatch(bulkTrash({ workspaceId, itemIds: [...selected] })).unwrap();
          showSuccess(dispatch, `${n} file${n === 1 ? '' : 's'} moved to Trash`);
          setSelected(new Set());
          dispatch(loadMediaSummary({ workspaceId }));
        } catch (err) {
          showError(dispatch, err || 'Bulk delete failed');
        }
      },
    });
  };

  const handleOpenSource = useCallback(
    (item) => {
      if (!item?.sourceUrl) {
        showError(dispatch, 'Source is no longer available');
        return;
      }
      navigate(item.sourceUrl);
    },
    [dispatch, navigate]
  );

  const handleDownload = useCallback(
    async (item) => {
      try {
        const { url, name } = await fetchMediaDownloadUrl(workspaceId, item._id);
        if (!url) throw new Error('No download URL returned');
        const a = document.createElement('a');
        a.href = url;
        a.download = name || item.name || 'download';
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch (err) {
        showError(dispatch, err?.message || 'Failed to download');
      }
    },
    [dispatch, workspaceId]
  );

  const handleBulkRestore = async () => {
    if (!selected.size) return;
    const ids = [...selected];
    const n = ids.length;
    try {
      const res = await dispatch(bulkRestore({ workspaceId, itemIds: ids })).unwrap();
      const restoredIds = (res?.restored || []).map((r) => r._id);
      const failed = res?.failed || [];
      dropFromSelection(restoredIds.length ? restoredIds : ids);
      if (restoredIds.length) {
        showSuccess(dispatch, `${restoredIds.length} file${restoredIds.length === 1 ? '' : 's'} restored`);
      }
      if (failed.length) {
        const quota = failed.find((f) => f.statusCode === 413);
        showError(dispatch, quota ? 'Some files could not be restored (storage limit)' : `${failed.length} restore${failed.length === 1 ? '' : 's'} failed`);
      }
      dispatch(loadMediaSummary({ workspaceId }));
    } catch (err) {
      showError(dispatch, err || 'Bulk restore failed');
    }
  };

  const handleBulkPurge = () => {
    if (!selected.size) return;
    const ids = [...selected];
    const n = ids.length;
    setConfirm({
      title: `Permanently delete ${n} file${n === 1 ? '' : 's'}?`,
      message: 'This cannot be undone. The files will be removed from storage forever.',
      confirmLabel: 'Delete forever',
      destructive: true,
      Icon: IconDeleteForever,
      onConfirm: async () => {
        setConfirm(null);
        try {
          const res = await dispatch(bulkPurge({ workspaceId, itemIds: ids })).unwrap();
          const purgedIds = res?.purged || [];
          const failed = res?.failed || [];
          dropFromSelection(purgedIds.length ? purgedIds : ids);
          if (purgedIds.length) {
            showSuccess(dispatch, `${purgedIds.length} file${purgedIds.length === 1 ? '' : 's'} permanently deleted`);
          }
          if (failed.length) {
            showError(dispatch, `${failed.length} delete${failed.length === 1 ? '' : 's'} failed`);
          }
          dispatch(loadMediaSummary({ workspaceId }));
        } catch (err) {
          showError(dispatch, err || 'Bulk delete failed');
        }
      },
    });
  };

  const handleEmptyTrash = () => {
    setConfirm({
      title: 'Permanently delete all trashed files?',
      message: 'This cannot be undone. The files will be removed from storage forever.',
      confirmLabel: 'Empty Trash',
      destructive: true,
      Icon: IconDeleteForever,
      onConfirm: async () => {
        setConfirm(null);
        try {
          await dispatch(emptyTrash({ workspaceId })).unwrap();
          showSuccess(dispatch, 'Trash emptied');
          dispatch(loadMediaSummary({ workspaceId }));
        } catch (err) {
          showError(dispatch, err || 'Failed to empty trash');
        }
      },
    });
  };

  const handleTrashWithConfirm = (item) => {
    setConfirm({
      title: `Move "${item.name}" to Trash?`,
      message: `Auto-deletes after ${summary?.retentionDays ?? 30} days. You can restore it from Trash before then.`,
      confirmLabel: 'Move to Trash',
      destructive: true,
      Icon: IconDelete,
      onConfirm: () => {
        setConfirm(null);
        handleTrash(item._id);
      },
    });
  };

  const allSelected = items.length > 0 && selected.size === items.length;
  const toggleSelectAllVisible = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(items.map((i) => i._id)));
  };

  const handleUpgrade = () => navigate(`/w/${shortId}/billing`);

  const [recalculating, setRecalculating] = useState(false);
  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      const res = await dispatch(recalculateUsage({ workspaceId })).unwrap();
      const delta = res?.delta || 0;
      const healed = res?.healed || 0;
      const parts = [];
      if (healed > 0) parts.push(`Fixed ${healed} file size${healed === 1 ? '' : 's'} (${formatBytes(res.healedBytes || 0)})`);
      if (delta !== 0) parts.push(`Total adjusted by ${formatBytes(Math.abs(delta))}`);
      showSuccess(dispatch, parts.length ? parts.join(' · ') : 'Usage already accurate');
      dispatch(loadMedia({ workspaceId, filters, page: pagination.page, limit: pagination.limit }));
      dispatch(loadMediaSummary({ workspaceId }));
    } catch (err) {
      showError(dispatch, err || 'Failed to recalculate');
    } finally {
      setRecalculating(false);
    }
  };

  const inTrash = filters.trash === '1';

  // Keyboard shortcuts. Only active when no input/textarea/contenteditable has
  // focus and no modal is open, so typing in the search field still works.
  useEffect(() => {
    const isTypingTarget = (el) => {
      if (!el) return false;
      if (el.isContentEditable) return true;
      const tag = el.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
    };
    const onKeyDown = (e) => {
      if (confirm || lightboxIndex >= 0) return;
      if (isTypingTarget(document.activeElement)) return;

      if (e.key === 'Escape' && selected.size > 0) {
        setSelected(new Set());
        return;
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selected.size > 0 && tab !== 'duplicates') {
        e.preventDefault();
        if (inTrash) {
          if (isAdmin) handleBulkPurge();
        } else {
          handleBulkTrash();
        }
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'a' && items.length > 0 && tab !== 'duplicates') {
        e.preventDefault();
        if (selected.size === items.length) setSelected(new Set());
        else setSelected(new Set(items.map((i) => i._id)));
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirm, lightboxIndex, selected, items, tab, inTrash, isAdmin]);
  const loading = status === 'loading';
  // Only show the skeleton on the very first load of this tab. Subsequent
  // refetches (e.g. switching back) keep the cached items visible.
  const showSkeleton = loading && !loadedOnce;
  const empty = !items.length && !loading;
  const hasActiveFilter = !!(filters.q || filters.source || filters.uploader || filters.uploadedFrom || filters.uploadedTo);
  // Hide the toolbar when the bucket is empty and no filter is active. The
  // empty state takes the page in that case.
  const showToolbar = !empty || hasActiveFilter;

  return (
    <div className="wm">
      {/* Hero */}
      <header className="wm__hero">
        <div className="wm__hero-text">
          <h1 className="wm__title">Media</h1>
          <p className="wm__subtitle">
            All attachments and covers from {workspace?.name || 'this workspace'}
          </p>
        </div>
      </header>

      {/* Tabs */}

      <div className="wm__header">
        <nav className="wm__tabs">
          <button
            className={`wm__tab ${tab === 'all' ? 'is-active' : ''}`}
            onClick={() => { setTab('all'); dispatch(setFilters({ trash: '0' })); }}
          >
            <IconGridView size={18} />
            All files
            {allCount > 0 && <span className="wm__tab-count">{allCount}</span>}
          </button>
          <button
            className={`wm__tab ${tab === 'trash' ? 'is-active' : ''}`}
            onClick={() => { setTab('trash'); dispatch(setFilters({ trash: '1' })); }}
          >
            <IconDelete size={18} />
            Trash
            {trashCount > 0 && (
              <span className="wm__tab-count is-warn">{trashCount}</span>
            )}
          </button>
          <button
            className={`wm__tab ${tab === 'duplicates' ? 'is-active' : ''}`}
            onClick={() => setTab('duplicates')}
          >
            <IconCopyAll size={18} />
            Duplicates
            {duplicates.totalGroups > 0 && (
              <span className="wm__tab-count is-warn">{duplicates.totalGroups}</span>
            )}
          </button>
        </nav>

        <div className="wm__hero-meter">
          <StorageMeter summary={summary} onUpgradeClick={handleUpgrade} />
        </div>
      </div>

      {!inTrash && summary?.trashedCount > 0 && (
        <div className="wm__retention-banner">
          <IconSchedule size={18} />
          <span>
            <strong>{summary.trashedCount}</strong> file{summary.trashedCount === 1 ? '' : 's'} in Trash —
            auto-deletes after {summary.retentionDays} days
          </span>
          <button className="wm__link" onClick={() => dispatch(setFilters({ trash: '1' }))}>
            View Trash →
          </button>
        </div>
      )}

      {tab === 'duplicates' && (
        <DuplicatesPanel
          duplicates={duplicates}
          onFilterChange={(patch) => dispatch(setDuplicateFilters(patch))}
          expandedGroup={expandedGroup}
          setExpandedGroup={setExpandedGroup}
          onOpenSource={handleOpenSource}
          onDownload={handleDownload}
          onTrashOne={handleTrashWithConfirm}
          onKeepOldest={(group) => {
            const oldest = group.items[0];
            const others = group.items.slice(1).map((i) => i._id);
            if (!others.length) return;
            setConfirm({
              title: `Keep oldest copy of "${group.name}"?`,
              message: `Trashes ${others.length} duplicate${others.length === 1 ? '' : 's'} (uploaded after ${formatDate(oldest.uploadedAt)}). Reclaims ${formatBytes(group.size * others.length)}.`,
              confirmLabel: `Trash ${others.length} copy${others.length === 1 ? '' : 'ies'}`,
              destructive: true,
              Icon: IconDelete,
              onConfirm: async () => {
                setConfirm(null);
                try {
                  await dispatch(bulkTrash({ workspaceId, itemIds: others })).unwrap();
                  showSuccess(dispatch, `Reclaimed ${formatBytes(group.size * others.length)}`);
                  dispatch(loadMediaSummary({ workspaceId }));
                } catch (err) {
                  showError(dispatch, err || 'Failed to trash duplicates');
                }
              },
            });
          }}
          isAdmin={isAdmin}
          currentUserId={currentUserId}
        />
      )}

      {tab !== 'duplicates' && (<>
        {inTrash && (
          <div className="wm__retention-banner wm__retention-banner--inline">
            <IconSchedule size={18} />
            <span>
              Items in Trash auto-delete after <strong>{summary?.retentionDays ?? 30}</strong> days. Restore them before then to keep.
            </span>
            {isAdmin && trashCount > 0 && (
              <button
                className="wm__btn wm__btn--danger-solid"
                onClick={handleEmptyTrash}
                disabled={bulkInFlight}
                title="Empty Trash"
              >
                {bulkInFlight ? <IconSpinner size={16} className="wm__spin" /> : <IconDeleteForever size={16} />}
                {bulkInFlight ? 'Emptying…' : 'Empty Trash'}
              </button>
            )}
          </div>
        )}

        {showToolbar && (
          <div className="wm__toolbar">
            <div className="wm__search">
              <IconSearch size={18} />
              <input
                type="search"
                placeholder="Search files…"
                value={filters.q}
                onChange={(e) => onFilterChange('q', e.target.value)}
              />
            </div>

            <div className="wm__filters">
              <ZoobbeSelect
                className="wm__select"
                options={SOURCE_OPTIONS}
                value={filters.source}
                onSelect={(opt) => onFilterChange('source', opt.value)}
              />

              {uploaders.length > 0 && (
                <ZoobbeSelect
                  className="wm__select"
                  options={uploaderOptions}
                  value={filters.uploader}
                  onSelect={(opt) => onFilterChange('uploader', opt.value)}
                />
              )}

              <ZoobbeSelect
                className="wm__select"
                options={DATE_RANGE_OPTIONS}
                value={filterToDateRange(filters)}
                onSelect={(opt) => onDateRangeChange(opt.value)}
              />

              <ZoobbeSelect
                className="wm__select"
                options={SORT_OPTIONS}
                value={filters.sort}
                onSelect={(opt) => onFilterChange('sort', opt.value)}
              />

              <div className="wm__view-toggle" role="group" aria-label="View">
                <button
                  className={view === 'grid' ? 'is-active' : ''}
                  onClick={() => onViewChange('grid')}
                  aria-label="Grid view"
                  title="Grid view"
                >
                  <IconGridView size={18} />
                </button>
                <button
                  className={view === 'list' ? 'is-active' : ''}
                  onClick={() => onViewChange('list')}
                  aria-label="List view"
                  title="List view"
                >
                  <IconViewList size={18} />
                </button>
              </div>

              {!inTrash && isAdmin && (
                <button
                  className="wm__icon-btn"
                  onClick={handleRecalculate}
                  disabled={recalculating}
                  title="Recalculate storage usage"
                >
                  {recalculating ? <IconSpinner size={18} className="wm__spin" /> : <IconRefresh size={18} />}
                </button>
              )}

            </div>
          </div>
        )}

        {/* Selection bar (sticky when active) */}
        {selected.size > 0 && (
          <div className="wm__selection-bar">
            <button className="wm__icon-btn" onClick={() => setSelected(new Set())} title="Clear selection">
              <IconClose size={18} />
            </button>
            <span className="wm__selection-bar__count">{selected.size} selected</span>

            {items.length > 0 && (
              <button
                className="wm__selection-bar__action"
                onClick={toggleSelectAllVisible}
                title={allSelected ? 'Deselect all visible' : 'Select all visible'}
              >
                {allSelected ? <IconDeselect size={16} /> : <IconSelectAll size={16} />}
                {allSelected ? 'Deselect all' : `Select all (${items.length})`}
              </button>
            )}

            {!inTrash && (
              <button
                className="wm__btn wm__btn--danger"
                onClick={handleBulkTrash}
                disabled={bulkInFlight}
              >
                {bulkInFlight ? <IconSpinner size={18} className="wm__spin" /> : <IconDelete size={18} />}
                {bulkInFlight ? 'Moving…' : 'Move to Trash'}
              </button>
            )}

            {inTrash && (
              <button
                className="wm__btn"
                onClick={handleBulkRestore}
                disabled={bulkInFlight}
              >
                {bulkInFlight ? <IconSpinner size={18} className="wm__spin" /> : <IconRestore size={18} />}
                {bulkInFlight ? 'Restoring…' : 'Restore'}
              </button>
            )}

            {inTrash && isAdmin && (
              <button
                className="wm__btn wm__btn--danger"
                onClick={handleBulkPurge}
                disabled={bulkInFlight}
                title="Delete forever"
              >
                <IconDeleteForever size={18} />
                Delete forever
              </button>
            )}
          </div>
        )}

        {/* Content */}
        {showSkeleton ? (
          <div className={`wm__${view}`}>
            {Array.from({ length: view === 'grid' ? 8 : 6 }).map((_, i) => (
              <div key={i} className={view === 'grid' ? 'wm__card wm__card--skeleton' : 'wm__row wm__row--skeleton'} />
            ))}
          </div>
        ) : empty ? (
          <div className="wm__empty">
            <div className="wm__empty-icon">
              {inTrash ? <IconDelete size={32} /> : <IconFolderOpen size={32} />}
            </div>
            <h3>{inTrash ? 'Trash is empty' : 'No media yet'}</h3>
            <p>
              {inTrash
                ? 'Deleted files will appear here for 30 days before being permanently removed.'
                : 'Upload an attachment to a card or page and it will show up here.'}
            </p>
            {!inTrash && (
              <div className="wm__empty-actions">
                <button className="wm__btn" onClick={() => navigate(`/w/${shortId}`)}>
                  <IconGridView size={16} />
                  Open boards
                </button>
                <button className="wm__btn wm__btn--ghost" onClick={() => navigate(`/w/${shortId}/pages`)}>
                  <IconDescription size={16} />
                  Open pages
                </button>
              </div>
            )}
          </div>
        ) : view === 'grid' ? (
          <div className="wm__grid">
            {items.map((item) => {
              const canDelete = isAdmin || String(item.uploader?._id || item.uploader) === String(currentUserId);
              const img = isImage(item);
              const isSel = selected.has(item._id);
              const isPending = pendingSet.has(item._id);
              return (
                <div key={item._id} className={`wm__card ${isSel ? 'is-selected' : ''} ${isPending ? 'is-pending' : ''}`}>
                  <label className="wm__card-checkbox" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isSel}
                      onChange={(e) => toggleSelect(item._id, e)}
                    />
                  </label>

                  <button
                    type="button"
                    className="wm__card-thumb wm__card-thumb--button"
                    onClick={() => openLightboxForItem(item)}
                    aria-label={`Preview ${item.name}`}
                  >
                    {(() => {
                      const FileIco = fileIconFor(item);
                      return img && item.url ? (
                        <Thumb src={item.url} alt={item.name} FallbackIcon={FileIco} />
                      ) : (
                        <div className="wm__card-icon"><FileIco size={48} /></div>
                      );
                    })()}
                    {(() => {
                      const SrcIco = SOURCE_ICON[item.source];
                      return (
                        <span className={`wm__source-chip wm__source-chip--${item.source}`}>
                          <SrcIco size={13} />
                          {SOURCE_LABEL[item.source]}
                        </span>
                      );
                    })()}
                  </button>

                  <div className="wm__card-body">
                    <div className="wm__card-name" title={item.name}>{item.name}</div>
                    <div className="wm__card-meta">
                      <span>{formatBytes(item.size)}</span>
                      <span className="wm__dot">·</span>
                      <span>{formatDate(item.uploadedAt)}</span>
                    </div>
                  </div>

                  <div className="wm__card-actions">
                    {inTrash ? (
                      <>
                        <button
                          className="wm__btn wm__btn--ghost"
                          onClick={() => handleRestore(item._id)}
                          disabled={isPending}
                          title="Restore"
                        >
                          {isPending ? <IconSpinner size={18} className="wm__spin" /> : <IconRestore size={18} />}
                          {isPending ? 'Restoring…' : 'Restore'}
                        </button>
                        <span className="wm__purge-countdown" title="Auto-deletes">
                          {daysUntil(item.purgeAt)}d
                        </span>
                      </>
                    ) : (
                      <>
                        {item.sourceUrl && (
                          <button
                            className="wm__icon-btn"
                            onClick={() => handleOpenSource(item)}
                            title={`Open ${SOURCE_LABEL[item.source]}`}
                          >
                            <IconOpenInNew size={18} />
                          </button>
                        )}
                        <button
                          className="wm__icon-btn"
                          onClick={() => handleDownload(item)}
                          title="Download"
                        >
                          <IconDownload size={18} />
                        </button>
                        {canDelete && (
                          <button
                            className="wm__icon-btn wm__icon-btn--danger"
                            onClick={() => handleTrashWithConfirm(item)}
                            disabled={isPending}
                            title="Move to Trash"
                          >
                            {isPending ? <IconSpinner size={18} className="wm__spin" /> : <IconDelete size={18} />}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className={`wm__list ${inTrash ? 'is-trash' : ''}`}>
            <div className="wm__list-head">
              <div className="wm__row-check">
                <input
                  type="checkbox"
                  checked={selected.size === items.length && items.length > 0}
                  onChange={toggleSelectAll}
                />
              </div>
              <div className="wm__row-name">Name</div>
              <div className="wm__row-source">Source</div>
              <div className="wm__row-size">Size</div>
              <div className="wm__row-date">Uploaded</div>
              <div className="wm__row-by">By</div>
              {inTrash && <div className="wm__row-purge">Auto-delete</div>}
              <div className="wm__row-actions"></div>
            </div>
            {items.map((item) => {
              const canDelete = isAdmin || String(item.uploader?._id || item.uploader) === String(currentUserId);
              const img = isImage(item);
              const isSel = selected.has(item._id);
              const isPending = pendingSet.has(item._id);
              return (
                <div key={item._id} className={`wm__row ${isSel ? 'is-selected' : ''} ${isPending ? 'is-pending' : ''}`}>
                  <div className="wm__row-check">
                    <input
                      type="checkbox"
                      checked={isSel}
                      onChange={(e) => toggleSelect(item._id, e)}
                    />
                  </div>
                  <div className="wm__row-name">
                    <div className="wm__row-thumb">
                      {(() => {
                        const FileIco = fileIconFor(item);
                        return img && item.url
                          ? <Thumb src={item.url} alt="" FallbackIcon={FileIco} variant="row" />
                          : <FileIco size={18} />;
                      })()}
                    </div>
                    <button
                      type="button"
                      className="wm__row-name-btn"
                      onClick={() => openLightboxForItem(item)}
                    >
                      {item.name}
                    </button>
                  </div>
                  <div className="wm__row-source">
                    {(() => {
                      const SrcIco = SOURCE_ICON[item.source];
                      return (
                        <span className={`wm__source-chip wm__source-chip--${item.source} wm__source-chip--inline`}>
                          <SrcIco size={13} />
                          {SOURCE_LABEL[item.source]}
                        </span>
                      );
                    })()}
                  </div>
                  <div className="wm__row-size">{formatBytes(item.size)}</div>
                  <div className="wm__row-date">{formatDate(item.uploadedAt)}</div>
                  <div className="wm__row-by"><Uploader user={item.uploader} /></div>
                  {inTrash && <div className="wm__row-purge">{daysUntil(item.purgeAt)}d</div>}
                  <div className="wm__row-actions">
                    {inTrash ? (
                      <button
                        className="wm__btn wm__btn--ghost"
                        onClick={() => handleRestore(item._id)}
                        disabled={isPending}
                      >
                        {isPending && <IconSpinner size={16} className="wm__spin" />}
                        {isPending ? 'Restoring…' : 'Restore'}
                      </button>
                    ) : (
                      <>
                        {item.sourceUrl && (
                          <button
                            className="wm__icon-btn"
                            onClick={() => handleOpenSource(item)}
                            title={`Open ${SOURCE_LABEL[item.source]}`}
                          >
                            <IconOpenInNew size={18} />
                          </button>
                        )}
                        <button
                          className="wm__icon-btn"
                          onClick={() => handleDownload(item)}
                          title="Download"
                        >
                          <IconDownload size={18} />
                        </button>
                        {canDelete && (
                          <button
                            className="wm__icon-btn wm__icon-btn--danger"
                            onClick={() => handleTrashWithConfirm(item)}
                            disabled={isPending}
                            title="Move to Trash"
                          >
                            {isPending ? <IconSpinner size={18} className="wm__spin" /> : <IconDelete size={18} />}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {pagination.pages > 1 && (
          <div className="wm__pagination">
            <button
              className="wm__btn wm__btn--ghost"
              disabled={pagination.page <= 1}
              onClick={() =>
                dispatch(loadMedia({ workspaceId, filters, page: pagination.page - 1, limit: pagination.limit }))
              }
            >
              <IconChevronLeft size={18} />
              Previous
            </button>
            <span className="wm__page-indicator">
              Page <strong>{pagination.page}</strong> of {pagination.pages}
            </span>
            <button
              className="wm__btn wm__btn--ghost"
              disabled={pagination.page >= pagination.pages}
              onClick={() =>
                dispatch(loadMedia({ workspaceId, filters, page: pagination.page + 1, limit: pagination.limit }))
              }
            >
              Next
              <IconChevronRight size={18} />
            </button>
          </div>
        )}

      </>)}

      <MediaViewer
        items={items}
        currentIndex={Math.max(0, lightboxIndex)}
        isOpen={lightboxIndex >= 0 && items.length > 0}
        onClose={closeLightbox}
        onNext={nextLightbox}
        onPrev={prevLightbox}
      />

      <ConfirmDialog
        open={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        destructive={confirm?.destructive}
        Icon={confirm?.Icon}
        onConfirm={confirm?.onConfirm}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
};

export default WorkspaceMedia;
